'use client';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ACCEPT_IMAGE_MIMETYPE, ACCEPT_VIDEO_MIMETYPE } from '@/app/constants';
import { hysdk } from '@/app/utils/client';
import type { UseMediaUploadOptions } from './hooks/use-media-upload';
import { useMediaUpload } from './hooks/use-media-upload';

export interface HeadlessMediaUploaderProps extends UseMediaUploadOptions {
  /**
   * 是否支持视频上传
   * 如果为 false，则只支持图片上传（默认支持图片）
   */
  acceptVideo?: boolean;
  /**
   * 是否支持图片上传
   * 如果为 false，则只支持视频上传（默认支持图片）
   */
  acceptImage?: boolean;
  /**
   * 总数限制（图片+视频总数）
   * 如果传了 maxCount，则以 maxCount 为最大总数限制
   */
  maxCount?: number;
}

/**
 * Headless 媒体上传组件
 *
 * 只处理隐藏的 input 元素和点击选择文件的逻辑，不包含任何 UI。
 * 支持图片和视频上传，根据 acceptVideo 参数决定是否支持视频。
 *
 */
export const HeadlessMediaUploader = forwardRef<
  HTMLInputElement,
  HeadlessMediaUploaderProps & {
    /**
     * 渲染函数，接收 openFileSelector 函数用于触发文件选择和 uploadFile 函数用于处理文件上传
     */
    children?: (props: {
      openFileSelector: (e?: React.MouseEvent) => Promise<void>;
      uploadFile: (file: File) => Promise<void>;
    }) => React.ReactNode;
  }
>(
  (
    {
      disabled,
      acceptVideo = true,
      acceptImage = true,
      maxCount,
      maxImageCount,
      maxImageFileSize,
      isInitialSelectedImageRange,
      scene,
      getSelectedImage,
      maxVideoCount = 1,
      maxVideoDuration,
      minVideoDuration,
      maxVideoSize,
      validateVideoMetadata,
      videoAccept = ACCEPT_VIDEO_MIMETYPE,
      captureCoverImage,
      uploadCoverImage,
      allowTempMode,
      emitUploadingInTempMode,
      onChange,
      onMetadataReady,
      children,
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => fileInputRef.current as HTMLInputElement, []);

    // 使用媒体上传 Hook
    const { handleFile, handleFiles } = useMediaUpload({
      disabled,
      maxCount,
      maxImageCount,
      maxImageFileSize,
      isInitialSelectedImageRange,
      scene,
      getSelectedImage,
      maxVideoCount,
      maxVideoDuration,
      minVideoDuration,
      maxVideoSize,
      validateVideoMetadata,
      videoAccept,
      captureCoverImage,
      uploadCoverImage,
      allowTempMode,
      emitUploadingInTempMode,
      onChange,
      onMetadataReady,
    });

    // 处理文件选择器打开
    const handleOpenFileSelector = async (e?: React.MouseEvent) => {
      if (disabled) return;
      // 检查iOS权限，如果没有权限则不打开文件选择器
      if (hysdk.sniff.isViduApp && hysdk.sniff.os === 'ios') {
        const hasPermission = await hysdk.getAppPermissionInfo(['album', 'camera']);
        if (!hasPermission) {
          e?.stopPropagation();
          return;
        }
      }
      // 手动触发文件选择器
      if (fileInputRef.current) fileInputRef.current.click();
    };

    // 处理文件选择
    const handleFileChange = async (file: File) => {
      await handleFile(file);
    };

    // 构建 accept 属性
    const acceptTypes = [...(acceptImage ? ACCEPT_IMAGE_MIMETYPE : []), ...(acceptVideo ? videoAccept : [])].join(',');

    // 是否支持多选
    const multiple = Boolean(maxCount && maxCount > 1);

    return (
      <>
        {children?.({ openFileSelector: handleOpenFileSelector, uploadFile: handleFileChange })}
        <input
          hidden
          accept={acceptTypes}
          type="file"
          ref={fileInputRef}
          disabled={disabled}
          multiple={multiple}
          onChange={async (e) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;
            // 使用 handleFiles 处理多个文件，支持 batchIndex
            if (files.length > 1) {
              await handleFiles(files);
            } else {
              // 单个文件时使用 handleFile
              await handleFileChange(files[0]);
            }
            e.target.value = ''; // 清空文件输入
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </>
    );
  },
);

HeadlessMediaUploader.displayName = 'HeadlessMediaUploader';
